// I² Visual Identity v1 — R1.1 token swap (2026-04-29).
// Pure black (#000000) and pure white (#FFFFFF) eliminated from the palette.
// Backgrounds, surfaces, and text shift to off-pure shades — pure colors look
// "default", off-pure colors look "designed". Accent hues unchanged.
const DARK = {
  bg: "#0B0E1F",            // off-pure-black, navy-tinted
  surface: "#141833",       // solid surface for non-glass contexts
  surfaceHover: "#1B2042",
  border: "#252B47",
  borderActive: "#3B3BFF",
  accent: "#4F46E5",
  // accentText is a higher-luminance indigo for text-only contexts on
  // the gradient backdrop. The brand `accent` (#4F46E5) reads as
  // ~2.9:1 on the gradient's mid-violet (#1B1248) — fails WCAG AA
  // (needs 4.5:1). #818CF8 hits ~5.1:1 on dark backdrop while still
  // reading as the indigo brand hue, so the action-button bgs and
  // tinted chips can keep accent untouched while body text / labels
  // upgrade to accentText.
  accentText: "#818CF8",
  accentGlow: "rgba(79,70,229,0.3)",
  accentSoft: "rgba(79,70,229,0.1)",
  green: "#10B981",
  greenGlow: "rgba(16,185,129,0.3)",
  orange: "#F59E0B",
  orangeGlow: "rgba(245,158,11,0.2)",
  pink: "#EC4899",
  pinkGlow: "rgba(236,72,153,0.2)",
  cyan: "#06B6D4",
  cyanGlow: "rgba(6,182,212,0.2)",
  purple: "#A855F7",
  purpleGlow: "rgba(168,85,247,0.2)",
  text: "#F0F0F8",          // off-pure-white — bumped 2026-04-29 for glass legibility
  textMuted: "#C2C2D5",     // bumped from #8A8AA0 — pop on glass surfaces, no blend
  textDim: "#9090A8",       // bumped from #5A5A70 — labels/captions readable over orbs
};

const LIGHT = {
  bg: "#F5F2EC",            // warm off-pure-white, cream-tinted
  surface: "#FCFAF7",       // off-pure-white surface (no #FFFFFF)
  surfaceHover: "#EFEBE2",
  border: "#DCD8CE",
  borderActive: "#3B3BFF",
  accent: "#4F46E5",
  // Light theme: text body is dark, so a slightly DEEPER indigo
  // gives the contrast (#3730A3 ~ indigo-800).
  accentText: "#3730A3",
  accentGlow: "rgba(79,70,229,0.15)",
  accentSoft: "rgba(79,70,229,0.06)",
  green: "#059669",
  greenGlow: "rgba(5,150,105,0.2)",
  orange: "#D97706",
  orangeGlow: "rgba(217,119,6,0.15)",
  pink: "#DB2777",
  pinkGlow: "rgba(219,39,119,0.15)",
  cyan: "#0891B2",
  cyanGlow: "rgba(8,145,178,0.15)",
  purple: "#9333EA",
  purpleGlow: "rgba(147,51,234,0.15)",
  text: "#0A0F25",          // off-pure-black navy — slightly deeper for glass pop
  textMuted: "#3F455B",     // bumped from #5B6078 for legibility on light glass
  textDim: "#6B7285",       // bumped from #9CA3B4 — labels/captions readable
};

// Mutable COLORS object — all components reference this.
// Values are swapped in-place by applyTheme() so existing imports stay valid.
export const COLORS = { ...DARK };

let _currentMode = localStorage.getItem("theme") || "dark";

export function applyTheme(mode) {
  _currentMode = mode;
  const palette = mode === "light" ? LIGHT : DARK;
  Object.assign(COLORS, palette);
  localStorage.setItem("theme", mode);
  // Update background for flash-prevention (body may not exist during early module eval)
  if (document.body) document.body.style.background = palette.bg;
  document.documentElement.style.background = palette.bg;
}

export function getThemeMode() {
  return _currentMode;
}

// Apply stored preference on module load
applyTheme(_currentMode);

export const STATUS_COLORS = { live: COLORS.green, paused: COLORS.orange, draft: COLORS.textDim, error: COLORS.pink };

export const CHANNEL_ICONS = { whatsapp: "\u{1F4AC}", chat: "\u{1F310}", voice: "\u{1F4DE}", sms: "\u{1F4F1}", email: "\u2709" };
